import {createSelector} from '@reduxjs/toolkit'

import {includes} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

import {FDS_AUTH_LEVEL, FDSPhase, OrderAlreadyCreatedReasons} from '@web/core/constants'

import {OrderProcessPhase} from '../constants'
import {RootState} from '../slices/interfaces'

export const orderProcessorSelector = (state: RootState) => state.orderProcessor

export const orderProcessStepInfoSelector = createSelector(
    (state: RootState) => state.orderProcessor.phase,
    (phase) => {
        const eqOrderProcessStep = eq(phase)
        return {
            phase,
            isNotStarted: eqOrderProcessStep(OrderProcessPhase.IDLE) || eqOrderProcessStep(OrderProcessPhase.FINISH),
            isIdle: eqOrderProcessStep(OrderProcessPhase.IDLE),
            isStart: eqOrderProcessStep(OrderProcessPhase.START),
            isProcessing: eqOrderProcessStep(OrderProcessPhase.PROCESSING),
            isFinish: eqOrderProcessStep(OrderProcessPhase.FINISH),
        }
    },
)

export const orderRequestSelector = (state: RootState) => state.orderProcessor.orderRequest

export const processStepSelector = (state: RootState) => state.orderProcessor.currentProcessStep

/** 결제 프로세스 완료 상태 */
export const finishStatusInfoSelector = (state: RootState) => state.orderProcessor.finishStatus

export const fdsInfoSelector = createSelector(
    (state: RootState) => state.orderProcessor.fds,
    (state: RootState) => state.orderProcessor.requestStartOrderCount,
    (fds, requestStartOrderCount) => {
        const {phase, url, actionType, authentication} = fds
        const eqPhase = eq(phase)
        const {level, sessionKey, unavailablePaymentType} = authentication ?? {}

        return {
            phase,
            eqPhase: eq(phase),
            isProcessing: !(eqPhase(FDSPhase.IDLE) || eqPhase(FDSPhase.FINISH)),
            requestStartOrderCount,
            url,
            actionType,
            level,
            sessionKey,
            unavailablePaymentType: unavailablePaymentType ?? null,
        }
    },
)

/** 주문 No selector */
export const orderNoSelector = createSelector(
    (state: RootState) => state.orderProcessor.orderNo,
    (state: RootState) => state.orderProcessor.finishStatus,
    (orderNo, finishStatus): Maybe<string> => {
        if (orderNo) {
            return orderNo
        }
        if (
            finishStatus &&
            includes(finishStatus.code, OrderAlreadyCreatedReasons) &&
            finishStatus.content?.failure &&
            'reasonAttributes' in finishStatus.content.failure &&
            'orderNo' in finishStatus.content.failure.reasonAttributes &&
            typeof finishStatus.content.failure.reasonAttributes.orderNo === 'string'
        ) {
            return finishStatus.content.failure.reasonAttributes.orderNo
        }
        return null
    },
)

/** 승인 채널 selector */
export const applyChannelSelector = (state: RootState) => state.orderProcessor.applyChannel

export const channelSelector = (state: RootState) => state.orderProcessor.channel

export const orderProcessNoSelector = (state: RootState) => state.orderProcessor.orderProcessNo

export const autoPaySelector = (state: RootState) => state.orderProcessor.isAutoPay

export const urlInfoSelector = createSelector(
    orderProcessorSelector,
    orderRequestSelector,
    ({orderProcessNo, orderNo, applyChannel, applyOrderRequest, fds: {authentication}}, orderRequest) => {
        return {
            applyUrl: qs.stringifyUrl({
                url: `${orderRequest?.url?.applyBridgeUrl}/${orderProcessNo}${orderNo ? `/${orderNo}` : ''}`,
                query: {
                    // 선물하기
                    ...(applyChannel === 'MULTIPLE_GIFTING' ? {applyChannel} : {}),
                    // 일반결제 빌링키
                    ...(applyOrderRequest.approvalSeq ? {seq: applyOrderRequest.approvalSeq} : {}),
                    ...(applyOrderRequest.virtualAccountNo ? {van: applyOrderRequest.virtualAccountNo} : {}),
                    ...(applyOrderRequest.payExpirationDate ? {ped: applyOrderRequest.payExpirationDate} : {}),
                    ...(orderRequest?.url?.applyBridgeQuery ? orderRequest?.url?.applyBridgeQuery : {}),
                    // 3ds 인증 세션키
                    ...(authentication?.level === FDS_AUTH_LEVEL.THREE_DS && authentication.sessionKey
                        ? {tds: authentication.sessionKey}
                        : {}),
                },
            }),
            orderSheetUrl: window.location.href,
        }
    },
)
